import { FormID } from '@finxone-platform/form-action';
import { KycLevel, ProfileStateModel } from '@finxone-platform/shared/sys-config-types';
import {
  BUSINESS_SIGN_IN_UNSUPPORTED_MESSAGE,
  HostError,
  OTP_CODE_LENGTH,
  OTP_CODE_REJECTED_MESSAGE,
  OTP_RESEND_AFTER_SECONDS,
  OnboardBusinessRequest,
  OnboardBusinessResult,
  OnboardIndividualRequest,
  OnboardIndividualResult,
  OnboardingVerificationStates,
  WidgetHostHandlers,
  WidgetOnboardingCheckStatus,
  WidgetOnboardingFlow,
  WidgetOnboardingKind,
  WidgetOnboardingRequirements,
  WidgetOnboardingStatus,
  WidgetVerificationChannel,
  WidgetVerificationConfirmResult,
  WidgetVerificationSendResult,
  WidgetVerifyIdentityResult,
  buildOnboardingFlow,
  isTerminalCheckStatus,
  normaliseBusinessFollowUp,
  normaliseOnboardingCheckStatus,
  normaliseSubmitFollowUp,
  onboardingRequiredFields,
  outstandingPrerequisites,
  prerequisiteUnmetMessage,
  submitFollowUpOutcome,
  verificationExpiresAt,
} from '@finxone-platform/shared/app-sdk';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, combineLatest, lastValueFrom, map, timer } from 'rxjs';
import { UpdateFormDataActionWithId } from '../../../../lib/actions/form-submission.action';
import { GetProfile } from '../../../../lib/actions/user-profile.action';
import { IdentityVerificationService } from '../../../../lib/services/identity-verification/identity-verification.service';
import { MetadataService } from '../../../../lib/services/metadata-service/metadata-service.service';
import { FormActionState } from '../../../../lib/state/form-submision.state';
import { ProjectSettingsState } from '../../../../lib/state/project-settings.state';
import { ProfileState } from '../../../../lib/state/user-profile.state';
import { CustomWidgetAuthBridge } from './custom-widget-auth';
import {
  OnboardingVerificationMarkers,
  toBusinessOnboardingWireRequest,
  toOnboardBusinessResult,
  toOnboardIndividualResult,
  toOnboardIndividualWireRequest,
  toOnboardingVerificationStates,
  toWidgetOnboardingStatus,
} from './custom-widget-contract-mappers';

/**
 * The onboarding operations and the `onboardingStatus` topic.
 *
 * ## What this file is for
 *
 * Onboarding is the one domain where a widget must be led rather than merely
 * served: the steps have a mandatory order, the middle one expires, and getting
 * it wrong strands a customer mid-signup. So the bridge does three things a
 * plain operation wrapper would not:
 *
 * 1. **It keeps the OTP mechanics opaque.** The signed nonce fetched per send, the
 *    Cloudflare bot-check token, the 4-vs-6 digit split, the per-IP rate limit —
 *    all of it stays inside MetadataService, which is the same code the native OTP
 *    widget uses. A widget sees `verify.send` / `verify.confirm`, a code length and
 *    a resend delay.
 * 2. **It enforces the prerequisite gate before any HTTP call.** The rules come
 *    from the SDK's shared onboarding module, which the *mock* host also uses, so
 *    a widget that gets the ordering wrong fails identically offline and in
 *    production. The server's own check remains the authority; this only stops a
 *    widget producing a 403 by accident.
 * 3. **It shares the platform's verification state rather than keeping its own.**
 *    The markers live in the same form-state slice the native onboarding CTA
 *    reads (`FormID.ONBOARDING_FLOW`), so verifying on a native page and
 *    submitting from a widget — or the reverse — works, and the two flows cannot
 *    disagree about whether a customer is verified.
 *
 * ## What deliberately never reaches the widget
 *
 * - The single-use verification tokens the server mints at OTP time. They are the
 *   only thing binding an onboarding verification to this client (there is no
 *   authenticated user yet), so they are held host-side and attached to the
 *   submit here.
 * - The provider access token for identity verification, and the vendor session
 *   itself — see IdentityVerificationService.
 * - The auto-login access/refresh tokens an individual submit answers with. A
 *   widget can *ask* for them to be used (`onSuccess: 'signIn'`) and learns
 *   whether that worked (`signedIn`), but the tokens go straight from this file's
 *   HTTP response into the auth bridge's sign-in path and are never a field on
 *   anything that crosses the port.
 */

/**
 * How often the host re-reads the profile while a verification decision is
 * outstanding. Matches the native KYC widget's cadence
 * (kyc-provider.component.ts), because it is the same information arriving the
 * same way.
 *
 * **There is no push channel for this.** Provider decisions arrive as webhooks
 * into the backend; the frontend has no socket, so polling is not a shortcut, it
 * is the only mechanism available. Unlike the native widget this stops once the
 * statuses are terminal, so a failed or cancelled application does not poll
 * forever.
 */
export const ONBOARDING_STATUS_POLL_MS = 10_000;

/** The operations this bridge answers. */
export const CUSTOM_WIDGET_ONBOARDING_OPERATIONS = [
  'onboarding.flow',
  'onboarding.requirements',
  'onboarding.verify.send',
  'onboarding.verify.confirm',
  'onboarding.submitIndividual',
  'onboarding.submitBusiness',
  'onboarding.verifyIdentity',
] as const;

export interface CustomWidgetOnboardingDeps {
  store: Store;
  metadataService: MetadataService;
  /**
   * Runs the vendor identity session in a host overlay. Optional so a host that
   * grants no identity step need not wire it; onboarding.verifyIdentity then
   * fails fast with a clear message.
   */
  identityVerificationService?: IdentityVerificationService;
  /**
   * The session mechanics `onSuccess` needs, borrowed from the auth bridge so
   * both surfaces sign a customer in the same way and land them on the same page
   * (see custom-widget-auth.ts). Optional: without it `onSuccess` can only be
   * honoured as 'stay', and the result says so — `{ signedIn: false,
   * redirected: false }` — rather than claiming a redirect that never happened.
   */
  auth?: Pick<CustomWidgetAuthBridge, 'signInWithSession' | 'goToLogin'>;
  /** Injectable clock, for deterministic tests. */
  now?: () => number;
}

/** Widget channel -> the platform's OTP payload key. */
function toOtpPayload(channel: WidgetVerificationChannel, value: string) {
  return { fieldType: channel, fieldValue: { [channel]: value } };
}

/**
 * Pulls `retryAfterSeconds` out of a 429. The rate-limit guard puts it in both
 * the body and the `Retry-After` header; the body is preferred because the header
 * is not exposed to JS unless CORS allows it, and a missing number falls back to
 * the window length rather than to zero (which would invite an immediate retry).
 */
function toRetryAfterSeconds(error: unknown): number {
  const response = error as { error?: { retryAfter?: unknown }; headers?: { get(name: string): string | null } };
  const fromBody = Number(response?.error?.retryAfter);
  if (Number.isFinite(fromBody) && fromBody > 0) return Math.ceil(fromBody);
  const fromHeader = Number(response?.headers?.get?.('Retry-After'));
  if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.ceil(fromHeader);
  return OTP_RESEND_AFTER_SECONDS;
}

function isRateLimited(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 429;
}

export function buildCustomWidgetOnboardingBridge(deps: CustomWidgetOnboardingDeps): {
  /** Whether `operation` belongs to this bridge. */
  handles(operation: string): boolean;
  call(operation: string, payload: Record<string, unknown>): Promise<unknown>;
  topic: {
    get(): Promise<WidgetOnboardingStatus>;
    subscribe(push: (payload: WidgetOnboardingStatus) => void): () => void;
  };
} {
  const { store, metadataService } = deps;
  const now = deps.now ?? (() => Date.now());
  /**
   * Whether this host session performed a submit. Needed on top of the profile's
   * statuses because a provider may answer a submit with a status the platform
   * still reports as 'not-started', and because on a logged-out onboarding zone
   * there is no profile to read at all.
   */
  const submittedHere$ = new BehaviorSubject<boolean>(false);
  /** Which kind was submitted, so verifyIdentity can default its level. */
  let submittedKind: WidgetOnboardingKind = 'individual';

  function projectConfig() {
    return store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.projectConfig;
  }

  /**
   * The project's verification TTLs. Absent config falls back to the platform's
   * server-side default inside verificationExpiresAt — deliberately unlike the
   * native CTA, which skips expiry checks entirely when a TTL is missing and then
   * submits into a 403. The cache entry expires either way; the only choice is
   * whether the customer hears it from the widget or from the server.
   */
  function ttlMinutes(channel: WidgetVerificationChannel): number | undefined {
    const config = projectConfig();
    return channel === 'email'
      ? config?.email_config?.verified_contact_cache_ttl_minutes
      : config?.sms_config?.verified_contact_cache_ttl_minutes;
  }

  function onboardingFormData(): (OnboardingVerificationMarkers & Record<string, unknown>) | undefined {
    return store.selectSnapshot(FormActionState.getFormActionStateWithId(FormID.ONBOARDING_FLOW))?.formData;
  }

  function readVerified(
    formData: OnboardingVerificationMarkers | undefined,
  ): OnboardingVerificationStates {
    return toOnboardingVerificationStates(formData, {
      email: formData?.emailVerifiedAt
        ? verificationExpiresAt(formData.emailVerifiedAt, ttlMinutes('email'))
        : undefined,
      phone: formData?.phoneVerifiedAt
        ? verificationExpiresAt(formData.phoneVerifiedAt, ttlMinutes('phone'))
        : undefined,
    });
  }

  function currentVerified(): OnboardingVerificationStates {
    return readVerified(onboardingFormData());
  }

  function profile(): ProfileStateModel | undefined {
    return store.selectSnapshot(ProfileState.getProfile);
  }

  /**
   * Whether an application already exists. Derived from the platform's statuses
   * so it survives a reload, OR-ed with this session's own submit so a provider
   * that answers 'not-started' does not invite a duplicate application.
   */
  function isSubmitted(current: ProfileStateModel | undefined, submittedHere: boolean): boolean {
    if (submittedHere) return true;
    const kyc = normaliseOnboardingCheckStatus(current?.kycVerificationStatus);
    const rawKyb = current?.activeOrganisationKybStatus || current?.kybVerificationStatus;
    const kyb = rawKyb ? normaliseOnboardingCheckStatus(rawKyb) : null;
    return kyc !== 'not-started' || (kyb !== null && kyb !== 'not-started');
  }

  function identityStatus(
    current: ProfileStateModel | undefined,
    kind: WidgetOnboardingKind,
  ): WidgetOnboardingCheckStatus | undefined {
    if (kind === 'business') {
      const rawKyb = current?.activeOrganisationKybStatus || current?.kybVerificationStatus;
      return rawKyb ? normaliseOnboardingCheckStatus(rawKyb) : undefined;
    }
    return normaliseOnboardingCheckStatus(current?.kycVerificationStatus);
  }

  function requirements(kind: WidgetOnboardingKind): WidgetOnboardingRequirements {
    // The one runtime-readable source of mandatory extras in the whole platform.
    // Nothing else in the frontend reads it, and most projects do not set it —
    // hence `extraFieldsKnown`, which is the difference between "nothing to
    // collect" and "we cannot tell you".
    const declared =
      projectConfig()?.account_service?.thisbankConfig?.onboarding?.mandatoryExtraAttributesInputsValidation;
    const known = Array.isArray(declared);
    return {
      kind,
      requiredFields: onboardingRequiredFields(kind),
      extraFields: known ? [...declared] : [],
      extraFieldsKnown: known,
    };
  }

  function buildFlow(payload: Record<string, unknown>): WidgetOnboardingFlow {
    const kind: WidgetOnboardingKind = payload['kind'] === 'business' ? 'business' : 'individual';
    const current = profile();
    return buildOnboardingFlow({
      kind,
      email: payload['email'] === undefined ? undefined : String(payload['email']),
      phone: payload['phone'] === undefined ? undefined : String(payload['phone']),
      verified: currentVerified(),
      submitted: isSubmitted(current, submittedHere$.value),
      identityStatus: identityStatus(current, kind),
      requirements: requirements(kind),
      now: now(),
    });
  }

  /**
   * The gate. Runs before any HTTP call, and reports *which* steps are missing —
   * a submit button that can only say "not allowed" sends the customer hunting.
   */
  function assertPrerequisites(operation: string, email?: string, phone?: string): void {
    const outstanding = outstandingPrerequisites({
      verified: currentVerified(),
      email,
      phone,
      now: now(),
    });
    if (outstanding.length) {
      throw new HostError(
        'onboarding-prerequisite-unmet',
        prerequisiteUnmetMessage(operation, outstanding),
        { outstanding },
      );
    }
  }

  async function sendVerification(payload: Record<string, unknown>): Promise<WidgetVerificationSendResult> {
    const channel: WidgetVerificationChannel = payload['channel'] === 'phone' ? 'phone' : 'email';
    const value = String(payload['value'] ?? '').trim();
    if (!value) throw new HostError('host-error', 'onboarding.verify.send requires value');
    try {
      // generateOtpPhoneEmail owns the nonce fetch and the Turnstile header; no
      // part of that is re-implemented here, and none of it crosses the port.
      await lastValueFrom(metadataService.generateOtpPhoneEmail(toOtpPayload(channel, value)));
    } catch (error) {
      throw toVerificationHostError('onboarding.verify.send', channel, error);
    }
    return {
      channel,
      codeLength: OTP_CODE_LENGTH[channel],
      resendAfterSeconds: OTP_RESEND_AFTER_SECONDS,
    };
  }

  async function confirmVerification(
    payload: Record<string, unknown>,
  ): Promise<WidgetVerificationConfirmResult> {
    const channel: WidgetVerificationChannel = payload['channel'] === 'phone' ? 'phone' : 'email';
    const value = String(payload['value'] ?? '').trim();
    const code = String(payload['code'] ?? '').trim();
    if (!value) throw new HostError('host-error', 'onboarding.verify.confirm requires value');
    if (!code) throw new HostError('host-error', 'onboarding.verify.confirm requires code');
    try {
      // No userId: onboarding has no authenticated user, and passing one would
      // stop MetadataService retaining the single-use token the submit needs.
      await lastValueFrom(
        metadataService.verifyOtpPhoneEmail({ ...toOtpPayload(channel, value), verificationCode: code }),
      );
    } catch (error) {
      throw toVerificationHostError('onboarding.verify.confirm', channel, error);
    }

    const verifiedAt = new Date(now()).toISOString();
    // The platform's own markers, in the platform's own slice — see the file
    // docs on why this is not a private store.
    store.dispatch(
      new UpdateFormDataActionWithId(
        channel === 'email'
          ? { isEmailVerified: true, emailVerifiedAt: verifiedAt, verifiedEmail: value }
          : { isPhoneVerified: true, phoneVerifiedAt: verifiedAt, verifiedPhone: value },
        FormID.ONBOARDING_FLOW,
      ),
    );
    const expiresAt = verificationExpiresAt(verifiedAt, ttlMinutes(channel));
    pushStatus();
    return { channel, verified: true, expiresAt };
  }

  /**
   * Maps an OTP failure onto the widget-visible error. Only two outcomes are
   * distinguished, because only two are actionable: wait (rate limited) or
   * re-enter (rejected). Everything else is a generic host error — an OTP failure
   * message can name the SMS provider or the platform's own internals, and a
   * widget must not be handed either.
   */
  function toVerificationHostError(
    operation: string,
    channel: WidgetVerificationChannel,
    error: unknown,
  ): HostError {
    if (isRateLimited(error)) {
      return new HostError('rate-limited', `${operation}: too many requests for ${channel}`, {
        retryAfterSeconds: toRetryAfterSeconds(error),
      });
    }
    if (operation.endsWith('confirm')) {
      return new HostError('host-error', OTP_CODE_REJECTED_MESSAGE);
    }
    return new HostError('host-error', `${operation}: the ${channel} code could not be sent`);
  }

  async function submitIndividual(payload: Record<string, unknown>): Promise<OnboardIndividualResult> {
    const request = payload as unknown as Partial<OnboardIndividualRequest>;
    if (!request.firstName || !request.lastName || !request.dob) {
      throw new HostError(
        'host-error',
        'onboarding.submitIndividual requires firstName, lastName and dob',
      );
    }
    if (!request.email || !request.phone) {
      throw new HostError('host-error', 'onboarding.submitIndividual requires email and phone');
    }
    const address = request.address;
    if (!address?.country || !address?.addressLine1 || !address?.city || !address?.postalCode) {
      throw new HostError(
        'host-error',
        'onboarding.submitIndividual requires address country, addressLine1, city and postalCode',
      );
    }
    // Gate first: nothing is sent if a step is outstanding.
    assertPrerequisites('onboarding.submitIndividual', request.email, request.phone);

    /**
     * The single-use tokens the server minted at OTP time, from the one chokepoint
     * that stashes them (MetadataService, which is also what the native flow
     * reads). They are host-held throughout: they never crossed to the widget,
     * and they go on the submit as top-level fields — never inside `extra`,
     * which is persisted onto the Keycloak profile.
     *
     * A missing one is the same failure as an unverified channel (the server
     * answers both with PROFILE_OTP_VERIFICATION_REQUIRED), so it is reported the
     * same way and before any HTTP call, naming the channel to re-verify.
     */
    const tokens = metadataService.getOnboardingVerificationTokens?.() ?? {};
    const untokenised: ('verify.email' | 'verify.phone')[] = [];
    if (!tokens.emailVerificationToken) untokenised.push('verify.email');
    if (!tokens.phoneVerificationToken) untokenised.push('verify.phone');
    if (untokenised.length) {
      throw new HostError(
        'onboarding-prerequisite-unmet',
        prerequisiteUnmetMessage('onboarding.submitIndividual', untokenised),
        { outstanding: untokenised },
      );
    }
    const wire = toOnboardIndividualWireRequest(request as OnboardIndividualRequest, tokens);
    const response = await lastValueFrom(metadataService.onboardIndividual(wire));
    submittedKind = 'individual';
    submittedHere$.next(true);
    // The server consumes the verification once the submit succeeds, so the
    // host's markers have to go too — leaving them would show a green tick for a
    // verification that no longer exists.
    clearVerificationMarkers();
    pushStatus();
    const result = toOnboardIndividualResult(response?.data ?? {});
    // The auto-login session, read from the host's own response and passed
    // straight to the auth bridge. It is never put on `result` — see the file
    // docs and the leak test in custom-widget-onboarding.spec.ts.
    return { ...result, ...applyFollowUp(normaliseSubmitFollowUp(request.onSuccess), response?.data) };
  }

  /**
   * Performs the `onSuccess` follow-up and reports what actually happened.
   *
   * Deliberately last, after the application exists: a redirect that fires before
   * the submit lands would take the customer away from a form whose data is gone.
   * And deliberately *not* fatal — the applicant has been created by the time this
   * runs, so a failure to redirect must not turn a successful submit into a
   * rejection. It downgrades to "we did not move you" instead, which the two
   * result flags can express honestly.
   */
  function applyFollowUp(
    followUp: ReturnType<typeof normaliseSubmitFollowUp>,
    session: { accessToken?: string; refreshToken?: string } | undefined,
  ): { signedIn: boolean; redirected: boolean } {
    if (followUp === 'stay') return submitFollowUpOutcome('stay', false);
    if (!deps.auth) {
      console.warn(`onboarding submit: onSuccess '${followUp}' ignored — no auth bridge on this host`);
      return submitFollowUpOutcome('stay', false);
    }
    try {
      // 'signIn' is best-effort platform-side: the tokens are generated after the
      // applicant exists and the response succeeds without them. No session means
      // the login page, which is a redirect without a sign-in.
      if (followUp === 'signIn' && deps.auth.signInWithSession(session ?? {})) {
        return submitFollowUpOutcome('signIn', true);
      }
      deps.auth.goToLogin();
      // Both branches land on the same pair — a redirect, and no sign-in.
      return submitFollowUpOutcome(followUp, false);
    } catch (error) {
      console.error('onboarding submit: the onSuccess follow-up failed', error);
      return submitFollowUpOutcome('stay', false);
    }
  }

  async function submitBusiness(payload: Record<string, unknown>): Promise<OnboardBusinessResult> {
    const request = payload as unknown as Partial<OnboardBusinessRequest>;
    if (!request.name) throw new HostError('host-error', 'onboarding.submitBusiness requires name');
    if (!request.identificationNumber) {
      throw new HostError('host-error', 'onboarding.submitBusiness requires identificationNumber');
    }
    const address = request.companyAddress;
    if (!address?.country || !address?.city || !address?.postalCode) {
      throw new HostError(
        'host-error',
        'onboarding.submitBusiness requires companyAddress country, city and postalCode',
      );
    }
    const members = request.members ?? [];
    if (!members.length) {
      throw new HostError('host-error', 'onboarding.submitBusiness requires at least one member');
    }
    // Rejected up front, with the SDK's shared message: this endpoint answers
    // `{ id, primaryUserId }` and mints no tokens, so there is no session for a
    // 'signIn' to consume. Silently downgrading it would teach a widget author a
    // parity that does not exist.
    const business = normaliseBusinessFollowUp(request.onSuccess);
    if (business.unsupported) throw new HostError('host-error', BUSINESS_SIGN_IN_UNSUPPORTED_MESSAGE);
    const primaries = members.filter((member) => member?.isPrimary);
    if (primaries.length !== 1) {
      throw new HostError(
        'host-error',
        'onboarding.submitBusiness requires exactly one member with isPrimary: true',
      );
    }
    // Validated here rather than left to the endpoint because the DTO's
    // transforms trim these unconditionally: a missing one is a 500, not a 400.
    for (const member of members) {
      if (
        !member?.position ||
        !member?.firstName ||
        !member?.lastName ||
        !member?.email ||
        !member?.phone ||
        !member?.dob
      ) {
        throw new HostError(
          'host-error',
          'onboarding.submitBusiness members require position, firstName, lastName, email, phone and dob',
        );
      }
    }
    // The same gate the server runs on the primary member — the person who
    // becomes the platform user. Checking here first just turns the server's
    // 403 into a pre-flight rejection naming the channel to re-verify.
    const primary = primaries[0];
    assertPrerequisites('onboarding.submitBusiness', primary.email, primary.phone);

    // The host-held single-use tokens, exactly as in submitIndividual above:
    // the endpoint requires both, a missing one is the same failure as an
    // unverified channel, and they never crossed to the widget.
    const tokens = metadataService.getOnboardingVerificationTokens?.() ?? {};
    const untokenised: ('verify.email' | 'verify.phone')[] = [];
    if (!tokens.emailVerificationToken) untokenised.push('verify.email');
    if (!tokens.phoneVerificationToken) untokenised.push('verify.phone');
    if (untokenised.length) {
      throw new HostError(
        'onboarding-prerequisite-unmet',
        prerequisiteUnmetMessage('onboarding.submitBusiness', untokenised),
        { outstanding: untokenised },
      );
    }

    const response = await lastValueFrom(
      metadataService.onboardBusiness(
        toBusinessOnboardingWireRequest(request as OnboardBusinessRequest, tokens),
      ),
    );
    submittedKind = 'business';
    submittedHere$.next(true);
    // The server consumes the verification on a successful submit, so the
    // host's markers go too — same rule as the individual submit.
    clearVerificationMarkers();
    pushStatus();
    // No session is possible here, so only the redirect flag can be true.
    const { redirected } = applyFollowUp(business.followUp, undefined);
    return {
      ...toOnboardBusinessResult(response ?? {}),
      redirected,
    };
  }

  function clearVerificationMarkers(): void {
    store.dispatch(
      new UpdateFormDataActionWithId(
        {
          isEmailVerified: false,
          emailVerifiedAt: null,
          verifiedEmail: null,
          emailVerificationToken: null,
          isPhoneVerified: false,
          phoneVerifiedAt: null,
          verifiedPhone: null,
          phoneVerificationToken: null,
        },
        FormID.ONBOARDING_FLOW,
      ),
    );
  }

  async function verifyIdentity(payload: Record<string, unknown>): Promise<WidgetVerifyIdentityResult> {
    if (!isSubmitted(profile(), submittedHere$.value)) {
      throw new HostError(
        'onboarding-prerequisite-unmet',
        prerequisiteUnmetMessage('onboarding.verifyIdentity', ['submit']),
        { outstanding: ['submit'] },
      );
    }
    if (!deps.identityVerificationService) {
      throw new HostError('host-error', 'onboarding.verifyIdentity is not available on this host');
    }
    const level = payload['level'] === 'business' || payload['level'] === 'individual'
      ? (payload['level'] as WidgetOnboardingKind)
      : submittedKind;
    try {
      // Resolves on launch, never on outcome: the decision arrives by webhook and
      // reaches the widget through the polled onboardingStatus topic.
      return await deps.identityVerificationService.launch(
        level === 'business' ? KycLevel.BUSINESS : KycLevel.PERSONAL,
      );
    } catch (error) {
      // Provider failures name provider internals; none of that is the widget's
      // business, so every one becomes the same host error — but it is logged
      // host-side rather than swallowed, because whoever operates the host needs
      // to know the vendor is refusing sessions.
      console.error('onboarding.verifyIdentity failed to launch a provider session', error);
      throw new HostError('host-error', 'onboarding.verifyIdentity could not start a verification session');
    }
  }

  function currentStatus(): WidgetOnboardingStatus {
    const current = profile();
    return toWidgetOnboardingStatus(current, {
      verified: currentVerified(),
      submitted: isSubmitted(current, submittedHere$.value),
      now: now(),
    });
  }

  /**
   * Nudges subscribers after a host-side change the store selectors cannot see
   * on their own (a submit recorded only in this session).
   */
  function pushStatus(): void {
    submittedHere$.next(submittedHere$.value);
  }

  function status$(): Observable<WidgetOnboardingStatus> {
    return combineLatest([
      store.select(ProfileState.getProfile),
      store.select(FormActionState.getFormActionStateWithId(FormID.ONBOARDING_FLOW)),
      submittedHere$,
    ]).pipe(
      map(([current, formState, submittedHere]) => {
        const formData = formState?.formData as OnboardingVerificationMarkers | undefined;
        return toWidgetOnboardingStatus(current as ProfileStateModel | undefined, {
          verified: readVerified(formData),
          submitted: isSubmitted(current as ProfileStateModel | undefined, submittedHere),
          now: now(),
        });
      }),
    );
  }

  return {
    handles: (operation) =>
      (CUSTOM_WIDGET_ONBOARDING_OPERATIONS as readonly string[]).includes(operation),
    call: async (operation, payload) => {
      switch (operation) {
        case 'onboarding.flow':
          return buildFlow(payload);
        case 'onboarding.requirements':
          return requirements(payload['kind'] === 'business' ? 'business' : 'individual');
        case 'onboarding.verify.send':
          return sendVerification(payload);
        case 'onboarding.verify.confirm':
          return confirmVerification(payload);
        case 'onboarding.submitIndividual':
          return submitIndividual(payload);
        case 'onboarding.submitBusiness':
          return submitBusiness(payload);
        case 'onboarding.verifyIdentity':
          return verifyIdentity(payload);
        default:
          throw new HostError('unknown-operation', `unknown-operation: ${operation}`);
      }
    },
    topic: {
      get: async () => currentStatus(),
      /**
       * Pushes on every relevant store change *and* polls, because the two carry
       * different information: the store change is a local one (a verification
       * just happened), while a provider decision only exists in the backend and
       * has to be fetched.
       *
       * Polling stops as soon as there is nothing left to learn — terminal
       * statuses, or an applicant who has not submitted anything. The native KYC
       * widget polls forever with no terminal state; this deliberately does not.
       */
      subscribe: (push) => {
        let latest: WidgetOnboardingStatus | undefined;
        const subscription = status$().subscribe((status) => {
          latest = status;
          push(status);
        });
        const poll = timer(ONBOARDING_STATUS_POLL_MS, ONBOARDING_STATUS_POLL_MS).subscribe(() => {
          if (!latest) return;
          const settled =
            isTerminalCheckStatus(latest.kyc) && (latest.kyb === null || isTerminalCheckStatus(latest.kyb));
          const nothingInFlight = latest.kyc === 'not-started' && latest.kyb === null;
          if (settled || nothingInFlight) {
            poll.unsubscribe();
            return;
          }
          store.dispatch(new GetProfile(false));
        });
        return () => {
          subscription.unsubscribe();
          poll.unsubscribe();
        };
      },
    },
  };
}

/**
 * Serves 'onboardingStatus' from the onboarding bridge and everything else from
 * the topics it wraps, so the widget still sees one data handler group.
 */
export function withOnboardingStatusTopic(
  topics: NonNullable<WidgetHostHandlers['data']>,
  onboarding: ReturnType<typeof buildCustomWidgetOnboardingBridge>['topic'],
): NonNullable<WidgetHostHandlers['data']> {
  return {
    get: (topic) => (topic === 'onboardingStatus' ? onboarding.get() : topics.get(topic)),
    subscribe: (topic, push) =>
      topic === 'onboardingStatus'
        ? onboarding.subscribe(push)
        : (topics.subscribe?.(topic, push) ?? (() => undefined)),
  };
}
